import { DateTime } from 'luxon';
import { PaginationRequest } from '../pagination/pagination';
import { User } from '../user/user';
import { OrderReturnStatus, OrderStatus } from '../order/order';
import { Payment } from '../payment/payment';
import { TaxCode } from '../tax/tax';
import { OnAccountStatus } from '../payment/on-account/on-account';

export enum InvoiceStatus {
  Draft = 'Draft',
  Void = 'Void',
  Approved = 'Approved',
}

export type Invoice = {
  id: string;
  customer: User;
  locationId: number;
  invoiceNumber?: string;
  status: InvoiceStatus;
  type: 'Order' | 'WorkOrder' | 'PurchaseOrder';
  documentType: 'Invoice' | 'CreditMemo' | 'PurchaseOrder';
  typeId: string;
  taxRate: number;
  taxCode?: TaxCode;
  taxAdjustment?: number;
  subtotalAdjustment?: number;
  totalAdjustment?: number;
  lineItems: LineItem[];
  payments: Payment[];
  subtotal: number;
  tax: number;
  total: number;
  balance: number;
  date?: DateTime;
  dueDate?: DateTime;
  locked: boolean;
  createdAt: DateTime;
  updatedAt: DateTime;
};

export type LineItemType =
  | 'OrderProduct'
  | 'ProductFee'
  | 'OrderFee'
  | 'Coupon'
  | 'DamageWaiver'
  | 'Delivery'
  | 'Pickup'
  | 'OrderPayment'
  | 'OrderRequest'
  | 'AdditionalDiscount'
  | 'Tax'
  | 'CreditCardFee'
  | 'LineItem';

export type LineItem = {
  id: string;
  invoiceId: string;
  type: LineItemType;
  typeId?: string;
  name: string;
  description?: string;
  unitPrice: number;
  quantity: number;
  total: number;
  taxable: boolean;
  taxOverride?: number;
  generalLedgerAccountId: string;
  taxCategoryId?: string;
  createdAt: DateTime;
  createdBy?: number;
  updatedAt: DateTime;
  updatedBy?: number;
  meta?: Record<string, any>;
};

export type InvoiceTaxMeta = {
  jurisdictionId: string;
  percentage: number;
  amount: number;
};

export type DateFilter = {
  from?: DateTime;
  to?: DateTime;
};
export type WorkflowStatus = 'Requests' | 'Approved' | 'On Rent' | 'Off Rent' | 'Completed' | 'Cancelled' | 'Quote';
export type SearchInvoiceRequest = {
  query?: string;
  date?: DateFilter;
  dueDate?: DateFilter;
  daysPastDue?: number;
  customerId?: number;
  addressId?: number;
  locationId?: number;
  invoiceStatusFilter?: ('Void' | 'Draft' | 'Payable' | 'Paid' | 'Past Due' | 'Pending Refund')[];
  orderStatusFilter?: OrderStatus;
  orderReturnStatusFilter?: OrderReturnStatus;
  invoiceIds?: string[];
  sortBy?: 'date' | 'dueDate' | 'invoiceNumber';
  sortDirection?: 'asc' | 'desc';
  workflowStatusFilter?: WorkflowStatus[];
  onAccountStatus?: OnAccountStatus.Approved | 'other';
} & PaginationRequest;

export type InvoiceSearchSummaryData = {
  pastDue: number;
  payable: number;
  received: number;
};

export type CreateLineItemRequest = Omit<
  LineItem,
  | 'id'
  | 'invoiceId'
  | 'type'
  | 'typeId'
  | 'total'
  | 'generalLedgerAccountId'
  | 'createdAt'
  | 'createdBy'
  | 'updatedAt'
  | 'updatedBy'
> & {
  generalLedgerAccountId?: string;
};

export type CustomerBalanceData = {
  customerId: number;
  pastDue: number;
  daysPastDue: number;
  due: number;
  totalDue: number;
  lifetimeTotal: number;
};

export type StatementPaymentSummary = { invoiceNumber?: string; invoiceId: string; amount: number; due: number };
